import React, { useState, useEffect } from "react";
import Logo from "../../../components/common/logo";
import slackIcon from "../../../assets/slack-icon.png";
import PopUp from "../../../components/common/popUp";
import ChannelSelector from "../../../components/common/channelSelector";
import { useSlackIntegration } from "../../../hooks/useSlackIntegration";
import checkIcon from '../../../assets/check-icon.svg';
import OnboardingCard from "../../../components/common/OnboardingCard";
import Toast from "../../../components/common/Toast";
import Header from "../../../components/common/Header";
import PreviewCard from "../../../components/common/PreviewCard";
import { useOrganization } from "../../../hooks/useOrganization";
import { useUsers } from "../../../hooks/useUsers";

interface ConnectSlackProps {
  onNext: () => void;
  onBack: () => void;
  currentStep: number;
  totalSteps: number;
}

const ConnectSlack: React.FC<ConnectSlackProps> = ({
  onNext,
  onBack,
  currentStep,
  totalSteps
}) => {
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [showChannelSelector, setShowChannelSelector] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [showError, setShowError] = useState(false);
  const { 
    status, 
    channels,
    isLoading, 
    isLoadingChannels,
    disconnect, 
    initiateSlackConnection,
    setChannel
  } = useSlackIntegration();
  const { organization } = useOrganization();
  const { user } = useUsers();

  useEffect(() => {
    if (status.connected && !status.channel_id && !isLoading) {
      setShowChannelSelector(true);
      setShowToast(true);
    }
  }, [status.connected, status.channel_id, isLoading]);

  const handleSkip = () => {
    if (status.connected) {
      disconnect();
    } else {
      setShowConfirmation(true);
    }
  };

  const handleConfirmSkip = () => {
    setShowConfirmation(false);
    onNext();
  };

  const handleConnectSlack = () => {
    if (status.connected) {
      if (status.channel_id) {
        onNext();
      } else {
        setShowChannelSelector(true);
      }
    } else {
      initiateSlackConnection();
    }
  };

  const handleChannelSelect = async (channelId: string) => {
    try {
      await setChannel(channelId, {
        onSuccess: () => {
          setShowChannelSelector(false);
          onNext();
        },
        onError: (error) => {
          console.error('Failed to set channel:', error);
        }
      });
    } catch (error) {
      console.error('Failed to set channel:', error);
    }
  };

  const getButtonText = () => {
    if (isLoading) return "Connecting...";
    if (status.connected) return "Continue";
    return "Connect with slack";
  };

  useEffect(() => {
    const handleKeyDown = async (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        try {
          // If connected to Slack but no channel selected, disconnect
          if (status.connected && !status.channel_id) {
            await disconnect();
          }
        } catch (error) {
          console.error('Failed to disconnect Slack on back:', error);
          setErrorMessage('Failed to disconnect Slack. Please try again.');
          setShowError(true);
        } finally {
          onBack();
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onBack, status.connected, status.channel_id, disconnect]);

  const leftPanelContent = (
    <div className="flex flex-col h-full">
      <div className="flex flex-col px-[16px] pt-[4px] pb-[16px]">
        <div className="mb-1">
          <div className="text-[32px] font-semibold mb-[36px] text-left">
            Sync your Slack
          </div>

          <div className="space-y-4 mb-8 text-left">
            <div className="flex items-center gap-3">
              <div className="w-6 h-6 rounded-full flex items-center justify-center">
                <img src={checkIcon} alt="check" className="w-[18px] h-[18px]" />
              </div>
              <p className="text-[#75777C] text-sm font-light">
                Instant lead updates via Slack notifications
              </p>
            </div>

            <div className="flex items-center gap-3">
              <div className="w-6 h-6 rounded-full flex items-center justify-center">
                <img src={checkIcon} alt="check" className="w-[18px] h-[18px]" />
              </div>
              <p className="text-[#75777C] text-sm font-light">
                Collaborate and share lead insights within Slack channels
              </p>
            </div>

            <div className="flex items-center gap-3">
              <div className="w-6 h-6 rounded-full flex items-center justify-center">
                <img src={checkIcon} alt="check" className="w-[18px] h-[18px]" />
              </div>
              <p className="text-[#75777C] text-sm font-light">
                Automate lead follow-ups, saving time and effort
              </p>
            </div>
          </div>

          <div className="space-y-4">
            <button
              onClick={handleConnectSlack}
              className="w-full h-[40px] bg-[#29DC5B] text-[#000000] rounded-[10px] flex items-center justify-center gap-3 text-base font-normal hover:bg-[#22c55e] transition-colors shadow-[0px_0px_0px_1px_#2325291A_inset]"
              disabled={isLoading}
            >
              <div className="w-6 h-6 bg-[#FFFFFF] rounded-lg flex items-center justify-center">
                <img src={slackIcon} alt="slack" className="w-[18px] h-[18px]" />
              </div>
              {getButtonText()}
            </button>

            <button   
              onClick={handleSkip}
              className="w-full text-[#9FA1A7] hover:text-[#7A7A7A] text-sm transition-colors"
            >
              {status.connected ? "Disconnect" : "Continue without sync"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen flex flex-col items-center">
      <OnboardingCard 
        leftPanel={leftPanelContent} 
        rightPanel={<PreviewCard 
          variant="slack" 
          showRole={true} 
          role={user?.role}
          companyName={organization?.linkedin_company_profile?.name || organization?.name}
        />}
        pageInfo={{
          currentPage: currentStep + 1,
          totalPages: totalSteps
        }}
      />
      
      <PopUp
        isOpen={showConfirmation}
        onClose={() => setShowConfirmation(false)}
        onConfirm={handleConfirmSkip}
        title="Are you sure?"
        message="Are you sure you want to continue without syncing your Slack?"
        cancelText="Cancel"
        confirmText="Yes, I'm sure"
      />

      <ChannelSelector
        isOpen={showChannelSelector}
        onClose={() => setShowChannelSelector(false)}
        onSelect={handleChannelSelect}
        channels={channels}
        isLoading={isLoadingChannels}
        selectedChannelId={status.channel_id}
      />

      <Toast 
        message="Slack connected successfully"
        isVisible={showToast}
        onClose={() => setShowToast(false)}
        type="success"
      />
    </div>
  );
};

export default ConnectSlack;
